import { useState, useEffect, useRef } from 'react';
import {
  getMediaList, getMediaUsage, getMediaUsageMap, deleteMedia, getMediaReferences,
  setMediaKept, uploadImage, generateExerciseImage, getMediaFileUrl, cleanupUnusedMedia,
} from '../api/media';
import AlertBanner from '../components/common/AlertBanner';
import LoadingSpinner from '../components/common/LoadingSpinner';

function formatBytes(bytes) {
  if (!bytes) return '0 MB';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

export default function MediaLibraryPage() {
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState([]);
  const [usage, setUsage] = useState({ used_bytes: 0, quota_bytes: 0, count: 0 });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const [aiName, setAiName] = useState('');
  const [refModal, setRefModal] = useState(null); // { media, refs }
  const [usedIds, setUsedIds] = useState(new Set());
  const [onlyUnused, setOnlyUnused] = useState(false);
  const [search, setSearch] = useState('');
  const fileRef = useRef(null);

  async function reload() {
    const [list, use, map] = await Promise.all([
      getMediaList('image'), getMediaUsage(), getMediaUsageMap(),
    ]);
    setMedia(list || []);
    setUsage(use || { used_bytes: 0, quota_bytes: 0, count: 0 });
    setUsedIds(new Set(map?.used_ids || []));
  }

  useEffect(() => {
    reload()
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await uploadImage(file);
      await reload();
      setSuccess('Bild hochgeladen.');
    } catch (err) {
      setError(err.message || 'Upload fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerate() {
    if (!aiName.trim()) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await generateExerciseImage(aiName.trim());
      await reload();
      setSuccess(`KI-Bild für „${aiName.trim()}" erzeugt.`);
      setAiName('');
    } catch (err) {
      setError(err.message || 'KI-Generierung fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleKept(m) {
    setError('');
    setSuccess('');
    try {
      await setMediaKept(m.id, !m.is_kept);
      await reload();
    } catch (err) {
      setError(err.message || 'Markierung fehlgeschlagen.');
    }
  }

  async function handleDelete(m) {
    setError('');
    setSuccess('');
    try {
      await deleteMedia(m.id);
      await reload();
      setSuccess('Bild gelöscht.');
    } catch (err) {
      // 409 → wird noch verwendet: Nachweise laden und Modal zeigen
      try {
        const refs = await getMediaReferences(m.id);
        setRefModal({ media: m, refs });
      } catch {
        setError(err.message || 'Löschen fehlgeschlagen.');
      }
    }
  }

  async function handleCleanup() {
    setError('');
    setSuccess('');
    setBusy(true);
    try {
      const preview = await cleanupUnusedMedia(true);
      const total = preview.deleted + preview.orphan_files;
      if (total === 0) {
        setSuccess('Nichts aufzuräumen.');
        return;
      }
      const parts = [`${preview.deleted} unbenutzte Bilder`];
      if (preview.orphan_files) parts.push(`${preview.orphan_files} verwaiste Dateien`);
      if (!window.confirm(`${parts.join(' und ')} (${formatBytes(preview.freed_bytes)}) endgültig löschen?`)) return;
      const res = await cleanupUnusedMedia();
      await reload();
      setSuccess(`Aufgeräumt: ${res.deleted + res.orphan_files} Dateien, ${formatBytes(res.freed_bytes)} frei.`);
    } catch (err) {
      setError(err.message || 'Aufräumen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  const pct = usage.quota_bytes ? Math.min(100, (usage.used_bytes / usage.quota_bytes) * 100) : 0;
  const barClass = pct > 90 ? 'bg-danger' : pct > 70 ? 'bg-warning' : 'bg-success';
  const isUnused = (m) => !usedIds.has(m.id) && !m.is_kept;
  const unusedCount = media.filter(isUnused).length;
  const matchesSearch = (m) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (m.file_name || '').toLowerCase().includes(q)
      || (m.meta_info?.exercise_name || '').toLowerCase().includes(q);
  };
  const displayed = media.filter((m) => (onlyUnused ? isUnused(m) : true) && matchesSearch(m));

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center pb-2 mb-4 border-bottom">
        <h1 className="h2">
          <i className="bi bi-images me-2" />
          Medien
        </h1>
      </div>

      {error && <AlertBanner message={error} type="danger" onDismiss={() => setError('')} />}
      {success && <AlertBanner message={success} type="success" onDismiss={() => setSuccess('')} />}

      {/* Speicheranzeige */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between small mb-1">
            <span className="fw-semibold">Speicher</span>
            <span className="text-muted">
              {formatBytes(usage.used_bytes)} von {formatBytes(usage.quota_bytes)} · {usage.count} Dateien
            </span>
          </div>
          <div className="progress" style={{ height: 10 }}>
            <div className={`progress-bar ${barClass}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {/* Aktionen */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-start">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Bild hochladen</label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="form-control"
                onChange={handleUpload}
                disabled={busy}
              />
            </div>
            <div className="col-md-8">
              <label className="form-label fw-semibold">KI-Bild generieren</label>
              <div className="input-group">
                <span className="input-group-text">Motiv</span>
                <input
                  type="text"
                  className="form-control"
                  value={aiName}
                  onChange={(e) => setAiName(e.target.value)}
                  placeholder="z. B. Liegestütze"
                  disabled={busy}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleGenerate}
                  disabled={busy || !aiName.trim()}
                >
                  {busy ? (
                    <><span className="spinner-border spinner-border-sm me-1" />…</>
                  ) : (
                    <><i className="bi bi-lightning-charge me-1" />Generieren</>
                  )}
                </button>
              </div>
              <div className="form-text">
                Nutzt das konfigurierte Mandanten-Bildmodell + Prompt (konsistenter Stil). Zählt aufs Kontingent.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Galerie-Toolbar */}
      {media.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
              <span className="input-group-text"><i className="bi bi-search" /></span>
              <input
                type="search"
                className="form-control"
                placeholder="Suchen (z. B. Liegestütz)…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="text-muted small">
              {unusedCount > 0 ? (
                <><i className="bi bi-exclamation-circle text-warning me-1" />{unusedCount} unbenutzt</>
              ) : (
                <><i className="bi bi-check-circle text-success me-1" />Alle Bilder werden verwendet</>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={handleCleanup}
              disabled={busy}
              title="Unbenutzte Bilder und verwaiste Dateien löschen"
            >
              <i className="bi bi-trash3 me-1" />Unbenutzte löschen{unusedCount > 0 ? ` (${unusedCount})` : ''}
            </button>
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="onlyUnused"
                checked={onlyUnused}
                onChange={(e) => setOnlyUnused(e.target.checked)}
                disabled={unusedCount === 0}
              />
              <label className="form-check-label small" htmlFor="onlyUnused">
                Nur unbenutzte anzeigen
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Galerie */}
      {media.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox d-block fs-1 mb-2" />
          Noch keine Bilder. Lade welche hoch oder generiere ein KI-Bild.
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-4 text-muted">
          <i className="bi bi-search d-block fs-3 mb-2" />
          {search.trim()
            ? <>Keine Treffer für „{search.trim()}".</>
            : 'Keine unbenutzten Bilder.'}
        </div>
      ) : (
        <div className="row g-3">
          {displayed.map((m) => {
            const linked = usedIds.has(m.id);   // in Übung/Workout oder als Logo verwendet
            const kept = !!m.is_kept;           // manuell als verwendet markiert
            const unused = !linked && !kept;
            return (
              <div key={m.id} className="col-6 col-md-4 col-lg-3">
                <div className={`card h-100${unused ? ' border-warning' : ''}`}>
                  <div className="position-relative">
                    <img
                      src={getMediaFileUrl(m.id)}
                      alt={m.file_name}
                      className="card-img-top"
                      style={{ aspectRatio: '1 / 1', objectFit: 'cover' }}
                    />
                    {unused && (
                      <span className="badge bg-warning text-dark position-absolute top-0 start-0 m-1">
                        Unbenutzt
                      </span>
                    )}
                    {kept && (
                      <span className="badge bg-info text-dark position-absolute top-0 start-0 m-1">
                        <i className="bi bi-pin-angle-fill me-1" />Behalten
                      </span>
                    )}
                  </div>
                  <div className="card-body p-2">
                    <div className="small text-truncate" title={m.file_name}>{m.file_name}</div>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {formatBytes(m.file_size)}
                      </span>
                      <div className="d-flex gap-1">
                        {!linked && (
                          <button
                            type="button"
                            className={`btn btn-sm ${kept ? 'btn-info' : 'btn-outline-secondary'}`}
                            onClick={() => toggleKept(m)}
                            title={kept ? 'Markierung „verwendet" entfernen' : 'Als verwendet markieren (z. B. Logo/Avatar)'}
                          >
                            <i className={`bi ${kept ? 'bi-pin-angle-fill' : 'bi-pin-angle'}`} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(m)}
                          title="Löschen"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* „Wird verwendet"-Modal */}
      {refModal && (
        <>
          <div className="modal show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-exclamation-triangle text-warning me-2" />
                    Bild wird noch verwendet
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setRefModal(null)} />
                </div>
                <div className="modal-body">
                  <p className="text-muted">
                    „{refModal.media.file_name}" kann nicht gelöscht werden, solange es verwendet wird.
                    Entferne es zuerst an diesen Stellen:
                  </p>
                  {refModal.refs.exercises?.length > 0 && (
                    <>
                      <div className="fw-semibold small">Übungen</div>
                      <ul>{refModal.refs.exercises.map((e) => <li key={`e${e.id}`}>{e.name}</li>)}</ul>
                    </>
                  )}
                  {refModal.refs.workouts?.length > 0 && (
                    <>
                      <div className="fw-semibold small">Workouts</div>
                      <ul>{refModal.refs.workouts.map((w) => <li key={`w${w.id}`}>{w.name}</li>)}</ul>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setRefModal(null)}>
                    Schließen
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setRefModal(null)} />
        </>
      )}
    </div>
  );
}
